<script setup lang="ts">
/**
 * EmailInviteModal — sends a calendar invite for a live event to an arbitrary
 * address. Pre-fills the recipient field with the current user's email, shows
 * a preview block with the event title, formatted start time, and course URL,
 * and submits via liveEventApi.sendEmailInvite.
 *
 * Parent controls visibility via `isOpen` and handles the emitted `close` /
 * `sent` events. Parent is responsible for reusing a single modal instance
 * across a list of events — pass the right event via props each time.
 */
import { computed, nextTick, ref, watch } from 'vue';
import axios from 'axios';
import { liveEventApi } from '@/services/liveEventApi';
import { useToastStore } from '@/stores';

interface Props {
  isOpen: boolean;
  liveEventId: number;
  eventTitle: string;
  /** ISO-8601 UTC timestamp. */
  scheduledAtUtc: string;
  /** Absolute URL to the course detail page. */
  courseUrl: string;
  /** Pre-filled recipient (typically the current user's email). */
  defaultEmail?: string;
}

const props = withDefaults(defineProps<Props>(), {
  defaultEmail: '',
});

const emit = defineEmits<{
  close: [];
  sent: [];
}>();

const toastStore = useToastStore();

const email = ref('');
const message = ref('');
const emailError = ref<string | null>(null);
const formError = ref<string | null>(null);
const sending = ref(false);
const emailInputRef = ref<HTMLInputElement | null>(null);

/**
 * "Thursday, May 28, 2026 at 12:00pm EDT" — matches the AC preview and the
 * backend email template's wording. Intl's `short` tz name gives abbreviations
 * like EDT/EST/PDT that calendar apps also display, so the preview lines up
 * with what the recipient will see once the invite is imported.
 */
const formattedDateTime = computed(() => {
  const date = new Date(props.scheduledAtUtc);
  const datePart = date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timePart = date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    })
    .replace(/\s?(AM|PM)/i, (_, meridiem) => meridiem.toLowerCase());
  return `${datePart} at ${timePart}`;
});

// Reset form state whenever the modal is re-opened or switched to a new event.
watch(
  () => [props.isOpen, props.liveEventId],
  async ([open]) => {
    if (!open) return;
    email.value = props.defaultEmail ?? '';
    message.value = '';
    emailError.value = null;
    formError.value = null;
    sending.value = false;
    await nextTick();
    emailInputRef.value?.focus();
    emailInputRef.value?.select();
  },
  { immediate: true }
);

function validateEmail(): boolean {
  const trimmed = email.value.trim();
  if (!trimmed) {
    emailError.value = 'Email address is required';
    return false;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    emailError.value = 'Please enter a valid email address';
    return false;
  }
  emailError.value = null;
  return true;
}

function handleClose() {
  if (sending.value) return;
  emit('close');
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.stopPropagation();
    handleClose();
  }
}

function mapError(err: unknown): string {
  if (!axios.isAxiosError(err)) return 'Failed to send invite. Please try again.';
  const status = err.response?.status;
  if (status === 429) {
    return 'You have hit the hourly limit for calendar invites. Please try again later.';
  }
  if (status === 404) {
    return 'This live event is no longer available.';
  }
  if (status === 400) {
    const data = err.response?.data as { detail?: string; message?: string } | undefined;
    return data?.detail ?? data?.message ?? 'The recipient address was rejected.';
  }
  if (status === 502) {
    return 'The email service is temporarily unavailable. Please try again in a moment.';
  }
  return 'Failed to send invite. Please try again.';
}

async function handleSubmit() {
  if (!validateEmail()) return;

  sending.value = true;
  formError.value = null;

  try {
    await liveEventApi.sendEmailInvite(props.liveEventId, {
      email: email.value.trim(),
      message: message.value.trim() || undefined,
    });
    toastStore.success('Invite sent');
    emit('sent');
    emit('close');
  } catch (err) {
    formError.value = mapError(err);
    console.error('Failed to send calendar invite:', err);
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <div
    v-if="isOpen"
    class="modal-overlay"
    @click.self="handleClose"
    @keydown="handleKeydown"
  >
    <div
      class="modal-container"
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-invite-title"
    >
      <div class="modal-header">
        <h2 id="email-invite-title">Email Calendar Invite</h2>
        <button
          class="modal-close-btn"
          type="button"
          aria-label="Close"
          :disabled="sending"
          @click="handleClose"
        >
          <bb-icon name="close" decorative></bb-icon>
        </button>
      </div>

      <form @submit.prevent="handleSubmit">
        <div class="modal-body">
          <bb-alert v-if="formError" variant="error">{{ formError }}</bb-alert>

          <div class="form-group">
            <label for="invite-email">Send to email address <span class="required">*</span></label>
            <input
              id="invite-email"
              ref="emailInputRef"
              v-model="email"
              type="email"
              autocomplete="email"
              placeholder="name@example.com"
              :class="{ error: emailError }"
              :disabled="sending"
              @input="emailError = null"
            />
            <span v-if="emailError" class="error-text">{{ emailError }}</span>
          </div>

          <div class="form-group">
            <label for="invite-message">Personal message (optional)</label>
            <textarea
              id="invite-message"
              v-model="message"
              rows="3"
              maxlength="500"
              placeholder="Add a note to include in the email..."
              :disabled="sending"
            />
          </div>

          <div class="invite-preview">
            <p class="invite-preview-label">The invite will include:</p>
            <div class="invite-preview-content">
              <p class="invite-preview-title">{{ eventTitle }}</p>
              <p class="invite-preview-date">{{ formattedDateTime }}</p>
              <p class="invite-preview-link">
                <bb-icon name="link" decorative></bb-icon>
                <span class="invite-preview-url">{{ courseUrl }}</span>
              </p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="bb-button -secondary"
            :disabled="sending"
            @click="handleClose"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="bb-button -primary"
            :disabled="sending"
          >
            <bb-icon v-if="sending" name="progress_activity" class="spinning" decorative></bb-icon>
            <bb-icon v-else name="send" decorative></bb-icon>
            {{ sending ? 'Sending…' : 'Send Invite' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border-light);
}

.modal-header h2 {
  margin: 0;
  font-family: var(--font-family-heading, 'Bitter', serif);
  font-size: 20px;
  font-weight: 600;
  color: var(--color-primary);
}

.modal-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: var(--radius-default);
  color: var(--color-text-muted);
  margin: -4px -4px 0 0;
}

.modal-close-btn:hover:not(:disabled) {
  background: var(--color-bg-hover);
}

.modal-close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.invite-preview {
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  padding: 12px 16px;
  background: var(--color-surface-secondary, #f8f9fa);
}

.invite-preview-label {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.invite-preview-content p {
  margin: 0 0 4px;
  font-size: 14px;
  color: var(--color-text-primary);
}

.invite-preview-content p:last-child {
  margin-bottom: 0;
}

.invite-preview-title {
  font-weight: 600;
}

.invite-preview-date {
  color: var(--color-text-secondary) !important;
}

.invite-preview-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-muted) !important;
  font-size: 13px !important;
}

.invite-preview-link bb-icon {
  font-size: 16px;
}

.invite-preview-url {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid var(--color-border-light);
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
